import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button, IconButton, Typography } from '@mui/material';
import {
  DEFAULT_CURRENCY,
  REVENUE_RECOGNITION_TYPE,
  type CustomerRevenueReportItem,
  type Currency,
  type RevenueRecognitionTypeOverride,
  type YearlyRevenue,
} from '@vy/types';
import { type FC, useCallback, useMemo, useState } from 'react';
import { Price } from '../../../../../components/design/Price';
import { VyEmptyState } from '../../../../../components/new-design/data-display/VyEmptyState';
import { NoSearchResults } from '@vy/ui-design-system';
import { ProductInspectDialog } from '../../../../../components/ProductInspectDialog';
import { useContractRevenueScheduleReport } from '../../../../../hooks/requests/use-contract-revenue-schedule-report';
import { buildMonthColumns } from './build-month-columns';
import { buildRowValues } from './build-row-values';

type Props = {
  contractId: string;
};

const PRICING_MODEL_TO_TYPE_LABEL: Record<string, string> = {
  Subscription: 'Straight-Line',
  CommercialTerms: 'Straight-Line',
  FixedOneTime: 'Point-in-time',
  Usage: 'Usage-Based',
  UsageGraduated: 'Usage-Based',
  UsageVolume: 'Usage-Based',
  UsageTiered: 'Usage-Based',
  UsageStairstep: 'Usage-Based',
};

const RECOGNITION_TYPE_OVERRIDE_TO_LABEL: Record<RevenueRecognitionTypeOverride, string> = {
  [REVENUE_RECOGNITION_TYPE.PointInTime]: 'Point-in-time',
  [REVENUE_RECOGNITION_TYPE.Milestones]: 'Milestones',
  [REVENUE_RECOGNITION_TYPE.StraightLine]: 'Straight-Line',
};

function getTypeLabel(item: CustomerRevenueReportItem): string {
  if (item.recognitionTypeOverride) {
    return RECOGNITION_TYPE_OVERRIDE_TO_LABEL[item.recognitionTypeOverride];
  }
  const pricingModel = item.variant?.pricingModel ?? null;
  if (!pricingModel) return '—';
  return PRICING_MODEL_TO_TYPE_LABEL[pricingModel] ?? 'Straight-Line';
}

function getVariantName(item: CustomerRevenueReportItem): string {
  return item.variant?.name ?? item.product?.name ?? item.variantId ?? 'Unknown';
}

const ROWS = [
  { label: 'Billed', key: 'scheduledCharge', carryForward: false },
  { label: 'Recognized', key: 'revenue', carryForward: false },
  { label: 'Deferred', key: 'deferredRevenue', carryForward: true },
  { label: 'Unbilled', key: 'unbilledRevenue', carryForward: true },
] as const;

const VariantRevenueTable: FC<{
  yearlyRevenue: YearlyRevenue;
  contract: CustomerRevenueReportItem['contract'];
  currency: Currency;
}> = ({ yearlyRevenue, contract, currency }) => {
  const columns = buildMonthColumns(yearlyRevenue, contract);

  if (columns.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No revenue data.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        overflowX: 'auto',
        scrollbarGutter: 'stable',
        scrollbarWidth: 'thin',
        scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { height: 8 },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0, 0, 0, 0)',
          borderRadius: 4,
          transition: 'background-color 150ms ease',
        },
        '&:hover': {
          scrollbarColor: 'rgba(0, 0, 0, 0.25) transparent',
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0, 0, 0, 0.25)' },
        },
      }}
    >
      <Box
        component="table"
        sx={{ borderCollapse: 'collapse', minWidth: '100%', '& th, & td': { px: 1.5, py: 1 } }}
      >
        <thead>
          <tr>
            <Box component="th" sx={{ textAlign: 'left', position: 'sticky', left: 0, backgroundColor: '#fff', zIndex: 1 }} />
            {columns.map((c) => (
              <Box
                component="th"
                key={c.label}
                sx={{ textAlign: 'left', fontWeight: 500, whiteSpace: 'nowrap', color: 'text.primary' }}
              >
                {c.label}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row) => {
            const values = buildRowValues(yearlyRevenue, columns, row.key, row.carryForward);
            return (
              <tr key={row.key}>
                <Box
                  component="td"
                  sx={{
                    position: 'sticky',
                    left: 0,
                    backgroundColor: '#fff',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    zIndex: 1,
                  }}
                >
                  {row.label}
                </Box>
                {columns.map((c, colIndex) => (
                  <Box component="td" key={c.label} sx={{ whiteSpace: 'nowrap' }}>
                    <Price currency={currency} fractionDigits={2}>
                      {values[colIndex]}
                    </Price>
                  </Box>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
};

export const ContractRevenueScheduleSection: FC<Props> = ({ contractId }) => {
  const { data, isLoading } = useContractRevenueScheduleReport(contractId, { suspense: true });
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => new Set());
  const [editingItem, setEditingItem] = useState<CustomerRevenueReportItem | null>(null);

  const toggleExpanded = useCallback((variantId: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(variantId)) next.delete(variantId);
      else next.add(variantId);
      return next;
    });
  }, []);

  const forceExpand = useCallback((variantId: string) => {
    setCollapsedIds((prev) => {
      if (!prev.has(variantId)) return prev;
      const next = new Set(prev);
      next.delete(variantId);
      return next;
    });
  }, []);

  const editingVariantId = useMemo(
    () => editingItem?.variantId ?? editingItem?.variant?.id ?? null,
    [editingItem],
  );

  if (isLoading || !data) {
    return <Typography variant="body2">Loading…</Typography>;
  }

  if (data.reportItems.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <VyEmptyState
          title="No revenue recognition rows"
          description="This contract has no revenue schedule."
          icon={<NoSearchResults />}
          titleSx={{ fontSize: '16px !important', fontWeight: 600 }}
          descriptionSx={{ fontSize: '14px !important', color: '#7E8796' }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 2 }}>
      {data.reportItems.map((item) => {
        const id = item.variantId ?? item.variant?.id ?? '';
        return (
          <VariantRevenueRow
            key={id}
            item={item}
            isExpanded={!collapsedIds.has(id)}
            onToggle={() => toggleExpanded(id)}
            onEdit={() => setEditingItem(item)}
          />
        );
      })}
      <ProductInspectDialog
        row={editingItem}
        contractId={contractId}
        onClose={() => setEditingItem(null)}
        onSuccess={() => {
          if (editingVariantId) forceExpand(editingVariantId);
        }}
      />
    </Box>
  );
};

type VariantRevenueRowProps = {
  item: CustomerRevenueReportItem;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
};

const VariantRevenueRow: FC<VariantRevenueRowProps> = ({ item, isExpanded, onToggle, onEdit }) => {
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          backgroundColor: '#F5F6FA',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          cursor: 'pointer',
        }}
        onClick={onToggle}
        data-vy="contract-revenue-schedule-variant-header"
      >
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
          <KeyboardArrowDownIcon
            sx={{
              width: 18,
              height: 18,
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 150ms ease',
            }}
          />
        </IconButton>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {getVariantName(item)}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
            Type:
          </Typography>
          <Button
            variant="text"
            size="small"
            endIcon={<EditIcon sx={{ width: 16, height: 16 }} />}
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            data-vy="contract-revenue-schedule-variant-edit"
            sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 500, minWidth: 'auto' }}
          >
            {getTypeLabel(item)}
          </Button>
        </Box>
      </Box>

      {isExpanded && (
        <Box sx={{ p: 2 }}>
          <VariantRevenueTable
            yearlyRevenue={item.yearlyRevenue}
            contract={item.contract}
            currency={item.customer.currency ?? DEFAULT_CURRENCY}
          />
        </Box>
      )}
    </Box>
  );
};
